import { ExpenseSplit, SplitMethod } from '../types';
import {
  getCurrencyDecimals,
  roundToCurrencyPrecision,
  sumsMatchInMinorUnits,
} from './currency-formatter';

export interface SplitOptions {
  preferUserId?: string;
}

/**
 * Who takes an equal split's leftover minor unit: the first payer (in payer
 * order) who is also in the split. `undefined` leaves it on the last member.
 */
export function oddCentRecipient(
  payerIds: readonly string[],
  splitUserIds: readonly string[]
): string | undefined {
  return payerIds.find(payerId => splitUserIds.includes(payerId));
}

export class ExpenseSplitter {
  /**
   * Split an expense amount among users based on the specified method
   */
  static splitExpense(
    amount: number,
    currency: string,
    method: SplitMethod,
    splits: Omit<ExpenseSplit, 'amount'>[],
    options?: SplitOptions
  ): ExpenseSplit[] {
    switch (method) {
      case 'equal':
        return this.splitEqual(amount, currency, splits, options?.preferUserId);
      case 'percentage':
        return this.splitByPercentage(amount, currency, splits);
      case 'shares':
        return this.splitByShares(amount, currency, splits);
      case 'custom':
        return this.splitCustom(amount, currency, splits);
      default:
        throw new Error(`Unsupported split method: ${method}`);
    }
  }

  /**
   * Split amount equally among all users
   */
  private static splitEqual(
    amount: number,
    currency: string,
    splits: Omit<ExpenseSplit, 'amount'>[],
    preferUserId?: string
  ): ExpenseSplit[] {
    const userCount = splits.length;
    if (userCount === 0) {
      throw new Error('Cannot split expense with no users');
    }

    const preferredIndex = splits.findIndex(
      split => split.userId === preferUserId
    );
    if (preferredIndex !== -1) {
      return this.splitEqualPreferring(
        amount,
        currency,
        splits,
        preferredIndex
      );
    }

    const baseAmount = roundToCurrencyPrecision(amount / userCount, currency);
    const remainderAmount = roundToCurrencyPrecision(
      amount - baseAmount * (userCount - 1),
      currency
    );

    return splits.map((split, index) => ({
      ...split,
      amount: index === userCount - 1 ? remainderAmount : baseAmount,
    }));
  }

  /**
   * Everyone gets the truncated share and the leftover minor units go one
   * each to the preferred user first, then from the last member back, so
   * the preferred user's share is never the smaller one.
   */
  private static splitEqualPreferring(
    amount: number,
    currency: string,
    splits: Omit<ExpenseSplit, 'amount'>[],
    preferredIndex: number
  ): ExpenseSplit[] {
    const factor = Math.pow(10, getCurrencyDecimals(currency));
    const sign = amount < 0 ? -1 : 1;
    const totalMinor = Math.round(Math.abs(amount) * factor);
    const baseMinor = Math.floor(totalMinor / splits.length);
    let leftover = totalMinor - baseMinor * splits.length;

    const extra = new Set<number>();
    if (leftover > 0) {
      extra.add(preferredIndex);
      leftover--;
    }
    for (let i = splits.length - 1; leftover > 0; i--) {
      if (extra.has(i)) continue;
      extra.add(i);
      leftover--;
    }

    return splits.map((split, index) => ({
      ...split,
      amount:
        // `|| 0` keeps a zero share from reading as -0.
        (sign * (baseMinor + (extra.has(index) ? 1 : 0))) / factor || 0,
    }));
  }

  /**
   * Split amount by percentage
   */
  private static splitByPercentage(
    amount: number,
    currency: string,
    splits: Omit<ExpenseSplit, 'amount'>[]
  ): ExpenseSplit[] {
    const totalPercentage = splits.reduce(
      (sum, split) => sum + (split.percentage || 0),
      0
    );

    if (Math.abs(totalPercentage - 100) > 0.01) {
      throw new Error('expenses:errors.percentagesMustTotal100');
    }

    let allocatedAmount = 0;
    const result = splits.map((split, index) => {
      const percentage = split.percentage || 0;
      let splitAmount: number;

      if (index === splits.length - 1) {
        // Last person gets the remainder to handle rounding
        splitAmount = roundToCurrencyPrecision(
          amount - allocatedAmount,
          currency
        );
      } else {
        splitAmount = roundToCurrencyPrecision(
          (amount * percentage) / 100,
          currency
        );
        allocatedAmount += splitAmount;
      }

      return {
        ...split,
        amount: splitAmount,
      };
    });

    return result;
  }

  /**
   * Split amount by shares (proportional to each user's share count)
   */
  private static splitByShares(
    amount: number,
    currency: string,
    splits: Omit<ExpenseSplit, 'amount'>[]
  ): ExpenseSplit[] {
    const totalShares = splits.reduce(
      (sum, split) => sum + (split.shares || 1),
      0
    );

    if (totalShares <= 0) {
      throw new Error('expenses:errors.sharesTotalMustBePositive');
    }

    let allocatedAmount = 0;
    const result = splits.map((split, index) => {
      const shares = split.shares || 1;
      let splitAmount: number;

      if (index === splits.length - 1) {
        // Last person gets the remainder to handle rounding
        splitAmount = roundToCurrencyPrecision(
          amount - allocatedAmount,
          currency
        );
      } else {
        splitAmount = roundToCurrencyPrecision(
          (amount * shares) / totalShares,
          currency
        );
        allocatedAmount += splitAmount;
      }

      return {
        ...split,
        amount: splitAmount,
      };
    });

    return result;
  }

  /**
   * Custom split - amounts are already specified
   */
  private static splitCustom(
    amount: number,
    currency: string,
    splits: Omit<ExpenseSplit, 'amount'>[]
  ): ExpenseSplit[] {
    // For custom splits, we expect the splits to already have amounts
    // but they come in as a different type, so we'll handle it properly
    const splitsWithAmount = splits as unknown as ExpenseSplit[];
    const totalSplitAmount = splitsWithAmount.reduce(
      (sum, split) => sum + (split.amount || 0),
      0
    );

    // Use currency-specific precision for validation
    const roundedTotal = roundToCurrencyPrecision(totalSplitAmount, currency);
    const roundedAmount = roundToCurrencyPrecision(amount, currency);

    if (Math.abs(roundedTotal - roundedAmount) > 0) {
      throw new Error('expenses:errors.customAmountsMustEqualTotal');
    }

    return splitsWithAmount.map(split => ({
      ...split,
      amount: roundToCurrencyPrecision(split.amount || 0, currency),
    }));
  }

  /**
   * Validate split configuration
   */
  static validateSplit(
    amount: number,
    currency: string,
    method: SplitMethod,
    splits: Omit<ExpenseSplit, 'amount'>[]
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (amount <= 0) {
      errors.push('expenses:errors.amountMustBePositive');
    }

    if (splits.length === 0) {
      errors.push('expenses:errors.atLeastOnePersonRequired');
    }

    switch (method) {
      case 'percentage': {
        const totalPercentage = splits.reduce(
          (sum, split) => sum + (split.percentage || 0),
          0
        );
        if (Math.abs(totalPercentage - 100) > 0.01) {
          errors.push('expenses:errors.percentagesMustTotal100');
        }
        // Check if any percentages are invalid (0 is valid since it means excluded)
        const hasInvalidPercentages = splits.some(
          split =>
            split.percentage === undefined ||
            split.percentage === null ||
            split.percentage < 0 ||
            split.percentage > 100
        );
        if (hasInvalidPercentages) {
          errors.push('expenses:errors.invalidPercentage');
        }
        break;
      }

      case 'custom': {
        const splitsWithAmount = splits as unknown as ExpenseSplit[];
        if (
          !sumsMatchInMinorUnits(
            splitsWithAmount.map(split => split.amount || 0),
            amount,
            currency
          )
        ) {
          errors.push('expenses:errors.customAmountsMustEqualTotal');
        }
        // Check if any amounts are invalid
        const hasInvalidAmounts = splitsWithAmount.some(
          split => split.amount === undefined || split.amount < 0
        );
        if (hasInvalidAmounts) {
          errors.push('expenses:errors.invalidAmount');
        }
        break;
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}
